import styles from "./portrait.module.scss";
import portrait0 from "./images/portrait0.png";
import { IMAGE_POSITIONS, Image } from "components/Common/Image/Image";
import { PickOrUploadModal } from "components/Common/PickOrUploadModal.js/PickOrUploadModal";
import classnames from "classnames/bind";
import { PencilIcon, Search } from "lucide-react";
import { Tooltip } from "components/Common/Tooltip/Tooltip";
import { usePortraitImages } from "../hooks/usePortraitImages";
import { usePreviewImageModal } from "contexts/PreviewImageModalContext";
import { usePingToChat } from "../hooks/usePingToChat";
import { CHAT_ENTRY_TYPES } from "components/OverlayButtons/ChatBox/chatConsts";
import { PingToChatIcon } from "../PingToChatIcon";

export const DEFAULT_PORTRAIT = portrait0;

const classNameBuilder = classnames.bind(styles);

export const Portrait = ({
  value,
  onClick,
  fetchingCharacters,
  portalProps,
  portalComponentProps,
  className,
  defaultValue = DEFAULT_PORTRAIT,
  defaultImagesPrefix,
  defaultImagesSuffix,
  defaultImages: propDefaultImages,
  defaultPickerName,
  imageContainerClassName,
  imageProps,
  shouldRenderPreview,
  overlayContent = "Click to change",
  forceShowOverlay,
  forceHoverButtons,
  shouldEditOnImageClick,
  shouldRenderChatPing,
}) => {
  const shouldRenderModal = Boolean(portalProps);
  const { onPingEntry } = usePingToChat();

  const portraitImages = usePortraitImages({
    prefix: defaultImagesPrefix,
    suffix: defaultImagesSuffix,
  });

  const onEditClick = (e) => {
    e?.stopPropagation();
    portalProps?.setIsPortalComponentRendered(true);
  };

  const { openPreviewImageModal } = usePreviewImageModal();

  const onOpenPreviewImagePortal = () => {
    openPreviewImageModal(value || defaultValue, {
      ...imageProps,
    });
  };

  return (
    <div className={classNameBuilder("container", className)}>
      {shouldRenderModal && (
        <>
          <PickOrUploadModal
            portalProps={portalProps}
            initialValue={value || defaultValue}
            title="Pick a Portrait"
            defaultName={defaultPickerName || "Wildsailor"}
            defaultImages={propDefaultImages || portraitImages}
            imageContainerClassName={classNameBuilder(
              "image-container",
              imageContainerClassName
            )}
            shouldDisableBackground
            imageProps={{
              position: IMAGE_POSITIONS.PORTRAIT,
              contain: false,
              aspectRatio: "1 / 1",
              style: {
                width: "fit-content",
                height: "fit-content",
                maxHeight: "100%",
                maxWidth: "100%",
              },
            }}
            {...portalComponentProps}
            key={portalComponentProps?.key}
          />
          <div
            className={classNameBuilder("icon", "edit", {
              "force-hover": forceHoverButtons,
            })}
            onClick={onEditClick}
          >
            <PencilIcon>
              <Tooltip position="top">Edit</Tooltip>
            </PencilIcon>
          </div>
        </>
      )}
      {shouldRenderPreview && (
        <div
          className={classNameBuilder("icon", "preview", {
            "force-hover": forceHoverButtons,
          })}
          onClick={(e) => {
            e.stopPropagation();
            onOpenPreviewImagePortal();
          }}
        >
          <Search />
          <Tooltip position="top-start">Preview</Tooltip>
        </div>
      )}
      {shouldRenderChatPing && (
        <div
          className={classNameBuilder("icon", "ping", {
            "force-hover": forceHoverButtons,
          })}
          onClick={(e) => {
            e.stopPropagation();
            onPingEntry({
              type: CHAT_ENTRY_TYPES.IMAGE,
              value: {
                src: value || defaultValue,
                shouldShowBackground: true,
              },
            });
          }}
        >
          <PingToChatIcon />
          <Tooltip position="top-start">Ping to Chat</Tooltip>
        </div>
      )}
      {!fetchingCharacters && (
        <Image
          src={value || defaultValue}
          alt="portrait"
          onClick={shouldEditOnImageClick ? onEditClick : onClick}
          position={IMAGE_POSITIONS.PORTRAIT}
          {...imageProps}
        />
      )}
    </div>
  );
};
